import React, { useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BaseUrl } from "../../helper/BaseUrl";
import contactData from "../../data/contactData.json";

export default function DynamicContactSection({ data }) {
  const id = useId();
  const navigate = useNavigate();
  const submitting = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [values, setValues] = useState({ name: "", phone: "", email: "", organisation: "", participants: "" });
  if (!data) return null;
  const phone = contactData.details.phone.replace(/[^+\d]/g, "");
  const fields = [
    { name: "name", label: "Name", placeholder: "Your Full Name", autoComplete: "name" },
    { name: "phone", label: "Mobile Number", placeholder: "10-Digit-Number", type: "tel", autoComplete: "tel-national", pattern: "[0-9]{10}", maxLength: 10 },
    { name: "email", label: "Email", placeholder: "you@company.com", type: "email", autoComplete: "email" },
    { name: "organisation", label: "Organisation", placeholder: "Where you're planning for this", autoComplete: "organization" },
  ];
  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues(previous => ({ ...previous, [name]: name === "phone" ? value.replace(/\D/g, "").slice(0, 10) : value }));
    setError("");
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    if (!values.name.trim() || !values.organisation.trim() || !values.phone || !values.email.trim() || !values.participants) {
      setError("Please complete all required fields.");
      return;
    }
    if (!/^\d{10}$/.test(values.phone)) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    submitting.current = true;
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${BaseUrl}/api/contact/store`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          form_type: "individual",
          name: values.name.trim(),
          phone: values.phone,
          email: values.email.trim(),
          organisation: values.organisation.trim(),
          looking_for: "Workshop / Team Session",
          preferred_date: "",
          location: "",
          comments: `Experience plan request. Participants: ${values.participants}`,
          source_url: sessionStorage.getItem("source_url") || "",
          current_url: window.location.href,
        }),
      });
      const responseText = await response.text();
      let result;
      try {
        result = responseText ? JSON.parse(responseText) : {};
      } catch {
        throw new Error("The server returned an unreadable response. Please try again.");
      }
      if (!response.ok || result.error !== false) throw new Error(result.message || "Unable to send your enquiry. Please try again.");
      navigate("/thank-you");
    } catch (submitError) {
      setError(submitError.message || "Unable to send your enquiry. Please try again.");
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };
  return (
    <section className="dynamic-contact-section" id="dynamic-contact" aria-labelledby={`${id}-title`} style={{ backgroundImage: `url(${data.backgroundImage})` }}>
      <div className="container">
        <div className="row align-items-center justify-content-between dynamic-contact-row">
          <div className="col-lg-6">
            <div className="dynamic-contact-content">
              <span className="main-title">{data.eyebrow}</span>
              <h2 className="heading" id={`${id}-title`}>{data.title}</h2>
              <p>{data.description}</p>
              <div className="dynamic-contact-links">
                <a className="dynamic-contact-whatsapp" href={`https://wa.me/${phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer">WhatsApp</a>
                <a href={`mailto:${contactData.details.email}`}>Email</a>
                <a href={`tel:${phone}`}>Call</a>
              </div>
            </div>
          </div>
          <div className="col-lg-5">
            <form className="dynamic-contact-form" onSubmit={handleSubmit} aria-label="Plan a team experience" aria-busy={loading}>
              <div className="dynamic-contact-fields">
                {fields.map(({ label, ...field }) => (
                  <div className={`dynamic-contact-field dynamic-contact-field-${field.name}`} key={field.name}>
                    <label htmlFor={`${id}-${field.name}`}>{label}<span aria-hidden="true">*</span></label>
                    <input {...field} id={`${id}-${field.name}`} type={field.type || "text"} value={values[field.name]} onChange={handleChange} required />
                  </div>
                ))}
                <div className="dynamic-contact-field dynamic-contact-field-participants">
                  <label htmlFor={`${id}-participants`}>Participants<span aria-hidden="true">*</span></label>
                  <select id={`${id}-participants`} name="participants" value={values.participants} onChange={handleChange} required>
                    <option value="" disabled>Select</option>
                    {data.participantOptions.map(option => <option key={option} value={option}>{option}</option>)}
                  </select>
                </div>
              </div>
              {error && <p className="dynamic-contact-error" role="alert">{error}</p>}
              <button type="submit" className="default-btn dynamic-contact-submit" disabled={loading}>{loading ? "Sending…" : data.buttonText}</button>
              <p className="dynamic-contact-note">{data.responseNote}</p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
